import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { RangeCalendar } from '../RangeCalendar';

const DatePickerContainer = styled.div`
  ${tw`relative mb-4 w-full`}
`;

const DateTimeContainer = styled.div`
  ${tw`flex gap-2`}
  width: 100%;
`;

const DatePickerButton = styled.button`
  ${tw`text-left rounded-lg bg-white p-3 flex items-center justify-between flex-1 h-[44px]`}
  border: 1px solid var(--color-border);
  color: var(--color-text-primary);
  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const TimePickerButton = styled.select`
  ${tw`rounded-lg bg-white cursor-pointer p-3 flex-1 h-[44px]`}
  border: 1px solid var(--color-border);
  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  width: 100%;
  max-width: 360px;
  padding: 12px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-menu);
`;

const keyOf = (date: Date) => DateTime.fromJSDate(date).toISODate() || '';
const dateOf = (key: string) => DateTime.fromISO(key).toJSDate();

interface BookingCalendarProps {
  disabled?: boolean;
  selectedDate: Date;
  showCalendar: boolean;
  timeOptions: string[];
  selectedTime: string;
  onDateChange: (date: Date) => void;
  onTimeChange: (time: string) => void;
  onToggleCalendar: () => void;
  minDate?: Date;
  maxDate?: Date;
  /** Fully blocked days (`yyyy-MM-dd`). */
  blocked?: Set<string>;
  /** Partly blocked days. */
  partial?: Set<string>;
  /** Zone used for "today". */
  timezone?: string | null;
}

/** One date + time field with a single-day popover calendar (shared RangeCalendar look). */
export const BookingCalendar = ({
  disabled = false,
  selectedDate,
  showCalendar,
  timeOptions,
  selectedTime,
  onDateChange,
  onTimeChange,
  onToggleCalendar,
  minDate,
  maxDate,
  blocked,
  partial,
  timezone,
}: BookingCalendarProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showCalendar) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        onToggleCalendar();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onToggleCalendar, showCalendar]);

  if (disabled) {
    return null;
  }

  const todayKey =
    (timezone ? DateTime.now().setZone(timezone) : DateTime.local()).toISODate() || '';
  const selectedKey = keyOf(selectedDate);
  const minKey = minDate ? keyOf(minDate) : todayKey;
  const maxKey = maxDate
    ? keyOf(maxDate)
    : DateTime.fromISO(minKey).plus({ years: 1 }).toISODate() || '';

  return (
    <DatePickerContainer>
      <DateTimeContainer>
        <DatePickerButton type="button" onClick={onToggleCalendar} aria-expanded={showCalendar}>
          <div>{selectedDate.toLocaleDateString()}</div>
          <span>
            <FontAwesomeIcon icon={showCalendar ? faCaretUp : faCaretDown} />
          </span>
        </DatePickerButton>
        <TimePickerButton value={selectedTime} onChange={e => onTimeChange(e.target.value)}>
          {timeOptions.map(time => (
            <option key={time} value={time}>
              {time}
            </option>
          ))}
        </TimePickerButton>
      </DateTimeContainer>
      {showCalendar && (
        <Popover ref={calendarRef}>
          <RangeCalendar
            mode="single"
            value={{ start: selectedKey, end: selectedKey }}
            onChange={next => {
              if (next.start) onDateChange(dateOf(next.start));
            }}
            minKey={minKey}
            maxKey={maxKey}
            todayKey={todayKey}
            blocked={blocked}
            partial={partial}
          />
        </Popover>
      )}
    </DatePickerContainer>
  );
};
